/**
 * TopBar — Atlas brand chrome.
 *
 * Three zones: brand (left) · project/order context (centre) · actions + account
 * (right). Terracotta lives in exactly one place in the chrome — the account
 * affordance and the primary Relatório action — so session state reads at a
 * glance. Aligned to earthbenders.pt: "earthbenders · análise territorial",
 * Inter Light wordmark, ghost "Entrar →" that routes to the existing login.
 *
 * Per Brand Book v2.0: dark (Basalt), Inter Light, terracotta as the one accent,
 * map is the protagonist (the bar is a bd-blurred overlay).
 */

import { useState } from 'react';
import { Download, Upload, ChevronDown, LogOut, ArrowRight, Loader2, FolderDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@core/authStore';
import { useAtlasStore } from '@/stores/atlasStore';
import { fetchOrderReport, fetchOrderGisPackage } from '@/services/api';
import { ProjectSwitcher } from './ProjectSwitcher';

type Props = {
  orderId: string | null;
  productsCount: number | null;
  areaHa: number | null;
  projectName?: string | null;
};

const ROLE_PT: Record<string, string> = {
  user: 'Cliente',
  backoffice: 'Equipa',
  admin: 'Administrador',
};

function initials(full?: string | null, email?: string | null): string {
  const n = (full || email || '?').trim();
  const parts = n.split(/\s+/).filter(Boolean);
  const s = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : n.slice(0, 2);
  return s.toUpperCase();
}

export function TopBar({ orderId, projectName }: Props) {
  const [reportBusy, setReportBusy] = useState(false);
  const [gisBusy, setGisBusy] = useState(false);
  const setCreateMode = useAtlasStore((s) => s.setCreateMode);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  async function handleReport() {
    if (!orderId || reportBusy) return;
    setReportBusy(true);
    const r = await fetchOrderReport(orderId);
    setReportBusy(false);
    if (r?.url) {
      window.open(r.url, '_blank', 'noopener');
    } else {
      alert('O relatório ainda não está disponível para este pedido.');
    }
  }

  async function handleGisPackage() {
    if (!orderId || gisBusy) return;
    setGisBusy(true);
    const r = await fetchOrderGisPackage(orderId);
    setGisBusy(false);
    if (r?.url) {
      window.open(r.url, '_blank', 'noopener');
    } else {
      alert('O pacote GIS ainda não está disponível para este pedido.');
    }
  }

  return (
    <header
      className="
        fixed inset-x-0 top-0 z-30 h-12
        bg-basalt/85 backdrop-blur-md
        border-b border-white/[0.08]
        flex items-center gap-2
        px-3 sm:px-4 lg:px-5
        select-none
      "
    >
      {/* Brand */}
      <div className="flex items-baseline gap-1.5 shrink-0">
        <span className="font-light text-limestone text-[15px] leading-none" style={{ letterSpacing: '-0.02em' }}>
          earthbenders
        </span>
        <span className="text-clay/50 text-sm leading-none" aria-hidden>·</span>
        <span
          className="hidden sm:inline font-light text-clay text-[13.5px] leading-none"
          style={{ letterSpacing: '-0.02em' }}
        >
          análise territorial
        </span>
      </div>

      <div className="flex-1" />

      {/* Project / order switcher (authenticated users only; self-hides) */}
      <ProjectSwitcher orderId={orderId} projectName={projectName ?? null} />

      {/* Actions */}
      <div className="flex items-center gap-0.5">
        {orderId && (
          <button
            type="button"
            onClick={handleReport}
            disabled={reportBusy}
            title="Descarregar relatório PDF"
            className="
              flex items-center gap-1.5 h-9 px-2.5 lg:px-3 rounded-md
              text-terracotta border border-terracotta/20
              hover:bg-terracotta/10 hover:border-terracotta/40
              disabled:opacity-60 disabled:cursor-wait
              transition-colors text-[13px]
            "
          >
            {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden lg:inline">{reportBusy ? 'A gerar…' : 'Relatório'}</span>
          </button>
        )}
        {orderId && (
          <button
            type="button"
            onClick={handleGisPackage}
            disabled={gisBusy}
            title="Descarregar pacote GIS (QGIS)"
            className="
              flex items-center gap-1.5 h-9 px-2.5 lg:px-3 rounded-md
              text-clay border border-white/10
              hover:text-limestone hover:bg-schist/60
              disabled:opacity-60 disabled:cursor-wait
              transition-colors text-[13px]
            "
          >
            {gisBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderDown className="w-4 h-4" />}
            <span className="hidden lg:inline">{gisBusy ? 'A gerar…' : 'Dados GIS'}</span>
          </button>
        )}
        {isAuthenticated && (
          <button
            type="button"
            onClick={() => setCreateMode(true)}
            title="Novo projeto"
            className="
              hidden md:flex items-center gap-1.5 h-9 px-2.5 lg:px-3 rounded-md
              text-clay hover:text-limestone hover:bg-schist/60
              transition-colors text-[13px]
            "
          >
            <Upload className="w-4 h-4" />
            <span className="hidden lg:inline">Novo projeto</span>
          </button>
        )}
      </div>

      <div className="w-px h-5 bg-white/10 mx-1" />

      <AccountCluster />
    </header>
  );
}

function AccountCluster() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profile = useAuthStore((s) => s.profile);
  const logout = useAuthStore((s) => s.logout);

  if (!isAuthenticated) {
    return (
      <button
        type="button"
        onClick={() => navigate('/login')}
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-md text-limestone hover:text-terracotta transition-colors text-[13.5px]"
      >
        Entrar
        <ArrowRight className="w-4 h-4 text-terracotta" />
      </button>
    );
  }

  const firstName = profile?.full_name?.split(' ')[0] || profile?.email || 'Conta';
  const role = ROLE_PT[(profile?.role as string) || 'user'] || 'Cliente';
  const avatar = (
    <span className="grid place-items-center rounded-full text-[11px] font-medium bg-terracotta/15 border border-terracotta/50 text-terracotta w-7 h-7">
      {initials(profile?.full_name, profile?.email)}
    </span>
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-9 pl-1 pr-1.5 rounded-full hover:bg-schist/60 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {avatar}
        <span className="hidden sm:inline text-limestone text-[13px]">{firstName}</span>
        <ChevronDown className="w-3.5 h-3.5 text-clay" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute right-0 top-12 z-50 w-60 bg-schist border border-white/[0.08] rounded-xl shadow-2xl shadow-black/40 overflow-hidden"
          >
            <div className="flex items-center gap-3 p-3.5">
              <span className="grid place-items-center rounded-full text-[12px] font-medium bg-terracotta/15 border border-terracotta/50 text-terracotta w-9 h-9">
                {initials(profile?.full_name, profile?.email)}
              </span>
              <div className="min-w-0">
                <div className="text-limestone text-[13.5px] truncate">{profile?.full_name || 'Conta'}</div>
                <div className="text-clay text-[12px] truncate">{profile?.email}</div>
              </div>
            </div>
            <div className="flex gap-1.5 px-3.5 pb-3">
              <span className="text-[11px] px-2 py-0.5 rounded border border-ochre/35 text-ochre bg-ochre/10">{role}</span>
              {profile?.company && (
                <span className="text-[11px] px-2 py-0.5 rounded border border-white/10 text-clay truncate max-w-[120px]">
                  {profile.company}
                </span>
              )}
            </div>
            <div className="h-px bg-white/[0.08]" />
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate('/login', { replace: true });
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13.5px] text-clay hover:text-terracotta hover:bg-white/[0.04] transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Terminar sessão
            </button>
          </div>
        </>
      )}
    </div>
  );
}
